/**
 * The profile page (M8-P2 §6.11), drawn to `[cap:my-profile]`: a left rail of 231 × 32 items
 * at x 56 from y 145 with a 32 px pitch, and a right pane whose avatar block is 180 × 136 at
 * (361, 170), whose name is 198 × 42, and whose empty fields are **"Add a …"** affordances
 * that become an input in place.
 *
 * monday's rail has six items; ours has five plus a disabled *Working status* that names the
 * milestone that owns it, rather than pretending the feature is missing.
 */
import { useEffect, useState } from 'react';
import '../admin/admin.css';

export interface ProfileMe {
  id: string;
  name: string;
  email: string;
  role: string;
  title: string | null;
  phone: string | null;
  mobilePhone: string | null;
  location: string | null;
  birthday: string | null;
  photoUrl: string | null;
  timezone: string | null;
  locale: string | null;
  theme: string;
  weekStart: string | null;
  dateFormat: string | null;
  teams: Array<{ id: string; name: string }>;
  /**
   * M0-F3b §8.4 — *Connected accounts*. Optional and defaulted everywhere it is read, so an
   * api that predates the field cannot blank the page (the lesson `teams` taught).
   */
  identities?: Array<{
    provider: string;
    subject: string;
    emailAtLink: string | null;
    linkedAt: string;
    lastLoginAt: string | null;
  }>;
}

export interface ProfileSource {
  me(): Promise<ProfileMe>;
  update(patch: Record<string, unknown>): Promise<ProfileMe>;
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<unknown>;
  sessions(): Promise<Array<{ id: string; ip: string | null; userAgent: string | null; lastSeenAt: string | null; current: boolean }>>;
  revokeSession(id: string): Promise<unknown>;
  logoutOthers(): Promise<{ revoked: number }>;
}

const SECTIONS = [
  { id: 'personal', label: 'Personal info', disabled: false },
  { id: 'working', label: 'Working status', disabled: true, milestone: 'M5' },
  { id: 'notifications', label: 'Notifications', disabled: false },
  { id: 'language', label: 'Language & region', disabled: false },
  { id: 'password', label: 'Password', disabled: false },
  { id: 'sessions', label: 'Session history', disabled: false },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/** monday's "Add a phone" affordance: a button that becomes an input in place. */
function Field(props: {
  label: string;
  value: string | null;
  placeholder: string;
  onSave: (value: string) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.value ?? '');
  useEffect(() => setDraft(props.value ?? ''), [props.value]);

  return (
    <div className="ez-profile__field">
      <label htmlFor={`ez-field-${props.label}`}>{props.label}</label>
      {editing ? (
        <input
          id={`ez-field-${props.label}`}
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft !== (props.value ?? '')) props.onSave(draft);
          }}
        />
      ) : (
        <button
          type="button"
          className={`ez-profile__field-value${props.value ? '' : ' ez-profile__field-value--empty'}`}
          onClick={() => setEditing(true)}
        >
          {props.value ?? props.placeholder}
        </button>
      )}
    </div>
  );
}

export function ProfilePage({ source }: { source: ProfileSource }): React.ReactElement {
  const [section, setSection] = useState<SectionId>('personal');
  const [me, setMe] = useState<ProfileMe | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Awaited<ReturnType<ProfileSource['sessions']>>>([]);

  useEffect(() => {
    void source.me().then(setMe).catch(() => undefined);
  }, [source]);
  useEffect(() => {
    if (section === 'sessions') void source.sessions().then(setSessions).catch(() => setSessions([]));
  }, [source, section]);

  const save = (patch: Record<string, unknown>): void => {
    void source.update(patch).then((next) => {
      setMe(next);
      setSaved(Object.keys(patch)[0] ?? null);
      setTimeout(() => setSaved(null), 1500);
    });
  };

  return (
    <div className="ez-profile" data-testid="profile-page">
      <nav className="ez-profile__rail" aria-label="Profile">
        {SECTIONS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="ez-profile__rail-item"
            aria-current={section === entry.id ? 'page' : undefined}
            disabled={entry.disabled}
            title={entry.disabled ? `Planned for ${'milestone' in entry ? entry.milestone : 'a later milestone'}` : undefined}
            data-testid={`profile-rail-${entry.id}`}
            onClick={() => !entry.disabled && setSection(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <main className="ez-profile__pane">
        {me === null ? null : section === 'personal' ? (
          <div data-testid="profile-personal">
            <div className="ez-profile__identity">
              <div className="ez-profile__avatar" data-testid="profile-avatar">
                <span aria-hidden className="ez-profile__avatar-disc" />
                <button type="button">Change profile picture</button>
              </div>
              <div>
                <h1 className="ez-profile__name" data-testid="profile-name">
                  {me.name}
                </h1>
                <div className="ez-profile__title">{me.title ?? 'Add a title'}</div>
                <span className="ez-admin-chip" data-testid="profile-role">
                  {me.role.charAt(0).toUpperCase() + me.role.slice(1)}
                </span>
              </div>
            </div>

            <div className="ez-profile__fields" data-testid="profile-fields">
              <Field label="Birthday" value={me.birthday} placeholder="Add a birthday" onSave={(value) => save({ birthday: value })} />
              <Field label="Email" value={me.email} placeholder="Add an email" onSave={() => undefined} />
              <Field label="Title" value={me.title} placeholder="Add a title" onSave={(value) => save({ title: value })} />
              <Field label="Phone" value={me.phone} placeholder="Add a phone" onSave={(value) => save({ phone: value })} />
              <Field label="Location" value={me.location} placeholder="Add a location" onSave={(value) => save({ location: value })} />
              <Field
                label="Mobile phone"
                value={me.mobilePhone}
                placeholder="Add a mobile phone"
                onSave={(value) => save({ mobilePhone: value })}
              />
            </div>

            {/* Defensive: an api that predates `Me.teams` must not blank the whole page. */}
            {(me.teams ?? []).length > 0 ? (
              <div data-testid="profile-teams" style={{ marginTop: 24 }}>
                <h3>My teams</h3>
                {(me.teams ?? []).map((team) => (
                  <span key={team.id} className="ez-admin-chip">
                    {team.name}
                  </span>
                ))}
              </div>
            ) : null}

            {/*
              * M0-F3b §8.4. Read-only on purpose: unlinking your own only way in is a support
              * call waiting to happen, so an admin does it from the Users directory.
              */}
            <div data-testid="profile-connected" style={{ marginTop: 24 }}>
              <h3>Connected accounts</h3>
              {(me.identities ?? []).length === 0 ? (
                <p className="ez-admin__hint" data-testid="profile-connected-empty">
                  Eli Z Platform — not linked. Signing in with the platform button on the login
                  page links your account.
                </p>
              ) : (
                (me.identities ?? []).map((identity) => (
                  <p key={identity.provider} className="ez-admin__hint" data-testid="profile-connected-row">
                    {`Eli Z Platform — linked as ${identity.subject}, since ${identity.linkedAt.slice(0, 10)}`}
                  </p>
                ))
              )}
            </div>

            {saved ? (
              <p role="status" data-testid="profile-saved">
                Saved
              </p>
            ) : null}
          </div>
        ) : section === 'language' ? (
          <div data-testid="profile-language">
            <h1 className="ez-admin__heading">Language &amp; region</h1>
            <div className="ez-admin-field">
              <label htmlFor="ez-tz">Timezone</label>
              <input id="ez-tz" defaultValue={me.timezone ?? ''} onBlur={(event) => save({ timezone: event.target.value })} />
            </div>
            <div className="ez-admin-field">
              <label htmlFor="ez-week">Week starts on</label>
              <select id="ez-week" defaultValue={me.weekStart ?? ''} onChange={(event) => save({ weekStart: event.target.value })}>
                <option value="">Account default</option>
                <option value="sunday">Sunday</option>
                <option value="monday">Monday</option>
              </select>
            </div>
            <div className="ez-admin-field">
              <label htmlFor="ez-date">Date format</label>
              <select id="ez-date" defaultValue={me.dateFormat ?? ''} onChange={(event) => save({ dateFormat: event.target.value })}>
                <option value="">Account default</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div className="ez-admin-field">
              <label htmlFor="ez-theme">Theme</label>
              <select id="ez-theme" defaultValue={me.theme} onChange={(event) => save({ theme: event.target.value })}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="night">Night</option>
              </select>
            </div>
          </div>
        ) : section === 'password' ? (
          <PasswordSection source={source} />
        ) : section === 'sessions' ? (
          <div data-testid="profile-sessions">
            <h1 className="ez-admin__heading">Session history</h1>
            <table className="ez-admin-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>IP</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((row) => (
                  <tr key={row.id}>
                    <td title={row.userAgent ?? ''}>{row.current ? 'This device' : (row.userAgent ?? 'Unknown')}</td>
                    <td>{row.ip ?? ''}</td>
                    <td>{row.lastSeenAt ?? ''}</td>
                    <td>
                      {row.current ? null : (
                        <button
                          type="button"
                          onClick={() => void source.revokeSession(row.id).then(() => source.sessions().then(setSessions))}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={() => void source.logoutOthers().then(() => source.sessions().then(setSessions))}>
              Log out of all other sessions
            </button>
          </div>
        ) : (
          <div data-testid="profile-notifications">
            <h1 className="ez-admin__heading">Notifications</h1>
            <p className="ez-admin__subheading">
              Notification preferences live on their own screen (M5): <a href="/me/notifications">open them</a>.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function PasswordSection({ source }: { source: ProfileSource }): React.ReactElement {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  return (
    <form
      data-testid="profile-password"
      onSubmit={(event) => {
        event.preventDefault();
        if (next !== confirm) {
          setMessage('The two new passwords do not match.');
          return;
        }
        void source
          .changePassword({ currentPassword: current, newPassword: next })
          .then(() => setMessage("Password updated. You've been signed out of your other sessions."))
          .catch((cause: unknown) => setMessage(cause instanceof Error ? cause.message : String(cause)));
      }}
    >
      <h1 className="ez-admin__heading">Password</h1>
      <div className="ez-admin-field">
        <label htmlFor="ez-current">Current password</label>
        <input id="ez-current" type="password" value={current} onChange={(event) => setCurrent(event.target.value)} />
      </div>
      <div className="ez-admin-field">
        <label htmlFor="ez-new">New password</label>
        <input id="ez-new" type="password" value={next} onChange={(event) => setNext(event.target.value)} />
      </div>
      <div className="ez-admin-field">
        <label htmlFor="ez-confirm">Confirm password</label>
        <input id="ez-confirm" type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} />
      </div>
      <button type="submit">Set password</button>
      {message ? <p role="status">{message}</p> : null}
    </form>
  );
}
